import { useUnWrapWSOL } from '@src/chains/solana/useUnWrapWSOL'
import { useWrapSOL } from '@src/chains/solana/useWrapSOL'
import { ChainId, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { StepNames } from '@src/enums'
import { useJupiterSwapIntegration } from '@src/hooks/useExchangeToken/useJupiterSwapIntegration'
import { analyticsService } from '@src/services/analytics'
import { ChainHandler, IExchangeData, SolanaSwapParams, UpdateData } from '@src/types/exchange'
import { IJupiterQuoteResponse } from '@src/types/jupiter'
import { isSolanaAddress } from '@src/utils'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('useJupiterSolanaHandler')

export function useJupiterSolanaHandler(): ChainHandler {
    const { setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { wrapSol, waitForWrap } = useWrapSOL()
    const { unwrapSol, waitForUnwrap } = useUnWrapWSOL()
    const { executeJupiterSwap } = useJupiterSwapIntegration()

    const handleApproval = (
        updateData: UpdateData,
        _sellData: IExchangeData,
        _walletAddress: string
    ): Promise<string> => {
        // Jupiter doesn't require a separate approval step - tokens are approved as part of the swap transaction
        logger.info('Jupiter swap does not require a separate approval step')

        // Update history to show we're skipping the approval step
        updateHistory(null, {
            ...updateData,
            currentStep: StepNames.CONFIRM_SWAP,
            result: {
                ...updateData.result,
                message: 'Jupiter swap does not require a separate approval step. Proceeding to swap.',
            },
        })

        // Return a dummy hash to indicate success
        return Promise.resolve('jupiter-approval-not-required')
    }

    const executeSwap = async (params: SolanaSwapParams, _referrer?: string): Promise<string> => {
        const { sellData, exchangeId, onSuccess, jupiterQuote, quote } = params

        try {
            logger.info('Executing Jupiter Solana swap', { params })

            // For Jupiter, we use the jupiterQuote property that was passed in the params
            if (!jupiterQuote) {
                throw new Error('Jupiter quote data is missing')
            }

            // Log detailed information about the Jupiter quote for debugging
            logger.info('Jupiter quote details', {
                hasJupiterQuote: !!jupiterQuote,
                quoteType: typeof jupiterQuote,
                quoteKeys: jupiterQuote ? Object.keys(jupiterQuote) : [],
                hasTxData: jupiterQuote && !!jupiterQuote.tx_data,
                txDataKeys: jupiterQuote && jupiterQuote.tx_data ? Object.keys(jupiterQuote.tx_data) : [],
                hasJupiterQuoteProperty: jupiterQuote && !!jupiterQuote.jupiter_quote,
                jupiterQuotePropertyKeys:
                    jupiterQuote && jupiterQuote.jupiter_quote ? Object.keys(jupiterQuote.jupiter_quote) : [],
                hasQuoteTxData: quote && !!quote.tx_data,
                quoteTxDataKeys: quote && quote.tx_data ? Object.keys(quote.tx_data) : [],
            })

            let signature: string | null = null
            let quoteWithTxData: IJupiterQuoteResponse

            // Check if we have the quote in the correct format (with tx_data)
            if (jupiterQuote.tx_data) {
                // If we already have tx_data, use the quote as is
                logger.info('Using jupiterQuote with tx_data directly')
                quoteWithTxData = jupiterQuote as IJupiterQuoteResponse
            } else if (quote && quote.tx_data) {
                // If the tx_data is in the quote object, use that
                logger.info('Using tx_data from quote object')
                quoteWithTxData = {
                    ...jupiterQuote,
                    tx_data: quote.tx_data,
                } as IJupiterQuoteResponse
            } else if (jupiterQuote.jupiter_quote && typeof jupiterQuote.jupiter_quote === 'object') {
                // Check if tx_data is in the jupiter_quote property
                if (jupiterQuote.jupiter_quote && 'tx_data' in jupiterQuote.jupiter_quote) {
                    logger.info('Found tx_data in jupiterQuote.jupiter_quote')
                    quoteWithTxData = {
                        ...jupiterQuote,
                        tx_data: jupiterQuote.jupiter_quote.tx_data,
                    } as IJupiterQuoteResponse
                } else {
                    logger.info('Using jupiterQuote.jupiter_quote but tx_data not found')
                    quoteWithTxData = {
                        ...jupiterQuote,
                        // If we don't have tx_data, we'll try to use the quote as is and let executeJupiterSwap handle the error
                    } as IJupiterQuoteResponse
                }
            } else {
                // We don't have the quote in any expected format
                logger.error('Jupiter quote is missing tx_data', { jupiterQuoteKeys: Object.keys(jupiterQuote) })
                throw new Error('Jupiter quote is missing tx_data and no alternative quote format was found')
            }

            // Execute the swap with the prepared quote
            signature = await executeJupiterSwap(quoteWithTxData, exchangeId)

            if (!signature) {
                throw new Error('Jupiter swap transaction was rejected or failed')
            }

            // Call onSuccess callback
            onSuccess()

            return signature
        } catch (error: any) {
            if (error.message.includes('User rejected the request')) {
                logger.info('User rejected the Jupiter swap transaction', { chainId: sellData.chainId })
                return ''
            }

            logger.error('Jupiter Solana swap failed:', error)
            throw error
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    const handleWrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating wrap operation for Jupiter swap', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
            })

            // Track wrap attempt
            analyticsService.trackWrapAttempt(
                'SOL', // Native token symbol
                'WSOL', // Wrapped token symbol
                sellData.chainId,
                amount.toString()
            )

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.DEPOSIT_WETH,
                result: { ...updateData.result, message: `Depositing ${amount} SOL.` },
            })

            if (WRAPPED_GAS_TOKENS[sellData.chainId] && isSolanaAddress(WRAPPED_GAS_TOKENS[sellData.chainId].address)) {
                const hash = await wrapSol(BigInt(amount))
                
                // Track wrap started
                analyticsService.trackWrapStarted(
                    'SOL', // Native token symbol
                    'WSOL', // Wrapped token symbol
                    sellData.chainId,
                    amount.toString(),
                    hash // Transaction hash
                )

                updateHistory(null, {
                    ...updateData,
                    currentStep: StepNames.DEPOSIT_WETH_PENDING,
                    result: {
                        ...updateData.result,
                        message: `Depositing ${amount} SOL.`,
                        sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                    },
                })

                await waitForWrap(hash)
                logger.success('Wrap operation completed for Jupiter swap', { hash })

                // Track wrap success
                analyticsService.trackWrapSuccess(
                    'SOL', // Native token symbol
                    'WSOL', // Wrapped token symbol
                    sellData.chainId,
                    amount.toString(),
                    hash // Transaction hash
                )

                return hash
            } else {
                logger.fatal(`No wrapped SOL token contract found for chainId ${sellData.chainId}`)
                
                // Track wrap failed
                analyticsService.trackWrapFailed(
                    'SOL', // Native token symbol
                    'WSOL', // Wrapped token symbol
                    sellData.chainId,
                    amount.toString(),
                    `No wrapped SOL token contract found for chainId ${sellData.chainId}` // Error message
                )
                
                throw new Error(
                    `Error wrapping SOL: No wrapped SOL token contract found for chainId ${sellData.chainId}`
                )
            }
        } catch (error: any) {
            if (error.message.includes('User rejected the request')) {
                logger.info('User rejected the wrap transaction for Jupiter swap', { chainId: sellData.chainId })
                
                // Track wrap failed - user rejected
                analyticsService.trackWrapFailed(
                    'SOL', // Native token symbol
                    'WSOL', // Wrapped token symbol
                    sellData.chainId,
                    amount.toString(),
                    'User rejected the transaction' // Error message
                )
                
                return ''
            }

            logger.error('Wrap operation failed for Jupiter swap', error)
            
            // Track wrap failed - other error
            analyticsService.trackWrapFailed(
                'SOL', // Native token symbol
                'WSOL', // Wrapped token symbol
                sellData.chainId,
                amount.toString(),
                error.message || 'Unknown error' // Error message
            )
            
            throw new Error('Error wrapping SOL. Please try again.')
        }
    }

    const handleUnwrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating unwrap operation for Jupiter swap', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
            })

            // Track unwrap attempt
            analyticsService.trackUnwrapAttempt(
                'WSOL', // Wrapped token symbol
                'SOL', // Native token symbol
                sellData.chainId,
                amount.toString()
            )

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH,
                result: { ...updateData.result, message: `Unwrapping ${amount} WSOL.` },
            })

            const hash = await unwrapSol(amount)
            
            // Track unwrap started
            analyticsService.trackUnwrapStarted(
                'WSOL', // Wrapped token symbol
                'SOL', // Native token symbol
                sellData.chainId,
                amount.toString(),
                hash // Transaction hash
            )
            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH_PENDING,
                result: {
                    ...updateData.result,
                    message: `Unwrapping ${amount} WSOL.`,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            await waitForUnwrap(hash)
            logger.info('Unwrap operation completed for Jupiter swap', { hash })

            // Track unwrap success
            analyticsService.trackUnwrapSuccess(
                'WSOL', // Wrapped token symbol
                'SOL', // Native token symbol
                sellData.chainId,
                amount.toString(),
                hash // Transaction hash
            )

            return hash
        } catch (error: any) {
            logger.error('Unwrap operation failed for Jupiter swap', error)

            // Track unwrap failed
            analyticsService.trackUnwrapFailed(
                'WSOL', // Wrapped token symbol
                'SOL', // Native token symbol
                sellData.chainId,
                amount.toString(),
                error.message || 'Unknown error' // Error message
            )

            throw new Error('Error unwrapping WSOL. Please try again.')
        }
    }

    return {
        executeSwap,
        handleApproval,
        handleWrap,
        handleUnwrap,
    }
}
